import type { BoardData, Port } from '../model/schema.js';
import { findPort } from '../trace/ports.js';
import { fieldColumns } from './columns.js';
import { portLabel } from './helpers.js';

export interface NetworkListRow {
  rowKey: string;
  connectionId: string;
  from: string;
  to: string;
  signal: string;
  cable: string;
  length: number | '';
  label: string;
}

const NETWORK_SIGNALS = new Set<Port['signal']>(['ethernet', 'dante', 'aes']);
const NETWORK_CONNECTORS = new Set<Port['connector']>(['rj45', 'ethercon']);

/** True for ports carrying network / digital snake traffic. */
export function isNetworkPort(port: Port | undefined): boolean {
  return (
    port !== undefined &&
    (NETWORK_SIGNALS.has(port.signal) || NETWORK_CONNECTORS.has(port.connector))
  );
}

/** Network list: every cable with a network/digital-snake port on either end, ordered by label. */
export function networkList(data: BoardData): NetworkListRow[] {
  return Object.values(data.connections)
    .flatMap((c): NetworkListRow[] => {
      const a = findPort(data, c.from.itemId, c.from.portId);
      const b = findPort(data, c.to.itemId, c.to.portId);
      if (!isNetworkPort(a) && !isNetworkPort(b)) return [];
      return [
        {
          rowKey: c.id,
          connectionId: c.id,
          from: portLabel(data, c.from.itemId, c.from.portId),
          to: portLabel(data, c.to.itemId, c.to.portId),
          signal: (isNetworkPort(a) ? a : b)!.signal,
          cable: c.cableType ?? '',
          length: c.length ?? '',
          label: c.label ?? '',
        },
      ];
    })
    .sort((x, y) => x.from.localeCompare(y.from) || x.rowKey.localeCompare(y.rowKey));
}

export const NETWORK_LIST_COLUMNS = fieldColumns<NetworkListRow>('network', [
  'from',
  'to',
  'signal',
  'cable',
  'length',
  'label',
]);
